import { BiConformedKey } from './catalog';
import { BiDatasetColumnRole, BiDatasetSchema } from './dataset';
import type { BiDisplayFormat } from './format';
import { BiAggregation } from './query';
import { BiFieldType } from './result';

// Mirror of backend `models/src/bi/upload.ts` (the wire types only) and of
// the generated enum `BiUploadStatus`.

export enum BiUploadStatus {
  Uploaded = 'uploaded',
  NeedsSheetSelection = 'needs_sheet_selection',
  Split = 'split',
  NeedsConfirmation = 'needs_confirmation',
  Confirmed = 'confirmed',
  Materialized = 'materialized',
  Ready = 'ready',
  Failed = 'failed',
  /** Left waiting for a sheet choice, answered elsewhere: nothing imported. */
  Expired = 'expired',
  Deleted = 'deleted',
}

/** Why a sheet of an uploaded file cannot be selected. */
export enum BiUploadSheetBlock {
  /** More rows than the store's limit. */
  RowLimit = 'row_limit',
  /** No data rows under a header. */
  Empty = 'empty',
  /** The sheet could not be read (no header, too wide, damaged). */
  Unreadable = 'unreadable',
}

/** Why an upload stopped, as far as the user can act on it. */
export enum BiUploadFailure {
  /** The file cannot be read: upload another. */
  File = 'file',
  /** The AI could not read the sheet's columns. */
  Interpret = 'interpret',
  /** Writing the dataset failed. */
  Materialize = 'materialize',
  Propose = 'propose',
}

/** A sheet upload: one selected sheet on its way to a dataset. */
export interface BiSheetUploadView {
  id: string;
  sheet: string;
  status: BiUploadStatus;
  /** Echo it on a confirmation; a stale one changes nothing. */
  generation: number;
  /** What the AI understood, from `needs_confirmation` on. */
  schema: BiDatasetSchema | null;
  datasetId: string | null;
  /** The proposals once `ready`: drafts, best first; may be empty. */
  proposalDraftIds: string[];
  failure: BiUploadFailure | null;
}

/** One sheet of the file, with the sheet upload made from it, if any. */
export interface BiUploadSheetView {
  name: string;
  rowCount: number;
  columnCount: number;
  /** The first few header labels, to recognize the sheet. */
  headers: string[];
  block: BiUploadSheetBlock | null;
  /** A dataset was already made from this sheet of the same file. */
  alreadyImported: boolean;
  upload: BiSheetUploadView | null;
}

/** A dataset made from the same file earlier, that the caller can use. */
export interface BiUploadDuplicate {
  datasetId: string;
  name: string;
  sheet: string | null;
  createdAt: string;
}

/** `GET /api/bi/uploads/:id`: a file upload and its sheet uploads. */
export interface BiUploadView {
  id: string;
  fileName: string;
  status: BiUploadStatus;
  intent: string | null;
  /** The store's row limit the sheets were measured against. */
  rowLimit: number | null;
  /** Datasets made from exactly this file before (same SHA-256). */
  duplicates: BiUploadDuplicate[];
  sheets: BiUploadSheetView[];
  failure: BiUploadFailure | null;
  createdAt: string;
  updatedAt: string | null;
}

/** A user's correction of one column, by its field key. */
export interface BiColumnOverride {
  key: string;
  label?: string;
  type?: BiFieldType;
  role?: BiDatasetColumnRole;
  defaultAgg?: BiAggregation;
  /** Null removes the format (the column reads as a plain number). */
  format?: BiDisplayFormat | null;
  /** Null removes the conformed key. */
  conformedKey?: BiConformedKey | null;
}

/** Mirror of backend `kTabularCsvSheetName`: a CSV's only sheet. */
export const kBiCsvSheetName = 'CSV';
export const kBiMaxDatasetDescription = 500;
/** Mirror of backend `kBiMaxText`, a column label's limit. */
export const kBiMaxColumnLabel = 200;
